import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { Sidebar, SIDEBAR_ITEM_ACTIVE_CLASS } from 'seagull';
import { AnchorLogo, Gift18, PlusCircleLine } from 'seagull/icons';
import { useGlobalContext, useStore, useStoreActions } from "../../../contexts";
import AddChargeModal from "../../AddChargeModal/AddChargeModal";
import { selectors } from "../../../store/state-selectors";
import { routes } from "../../../constants";
import { UserRole } from "../../../store/store.model";
import { notEmpty } from "../../../infra/utils";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { Intent } from "../../../infra/access-control";
import DisabledBillingTooltip from "../../DisabledBillingTooltip";
import styles from "./HomeSidebar.module.scss";
export const sidebarRoutes = [{
  id: 'dashboard',
  url: routes.vendorDashboard,
  text: 'Dashboard',
  shouldShow: _ref => {
    let {
      isVendor,
      hasBusiness
    } = _ref;
    return isVendor || !hasBusiness;
  }
}, {
  id: 'proposals',
  text: 'Proposals',
  shouldShow: _ref2 => {
    let {
      isVendor
    } = _ref2;
    return isVendor;
  },
  items: [{
    id: 'sent',
    url: routes.proposalsDashboard,
    text: 'Sent',
    shouldShow: _ref3 => {
      let {
        isVendor
      } = _ref3;
      return isVendor;
    }
  }, {
    id: 'drafts',
    url: routes.draftsDashboard,
    text: 'Drafts',
    shouldShow: _ref4 => {
      let {
        isVendor
      } = _ref4;
      return isVendor;
    }
  }, {
    id: routes.proposalTemplates,
    text: 'Templates',
    url: routes.proposalTemplates,
    shouldShow: _ref5 => {
      let {
        isVendor
      } = _ref5;
      return isVendor;
    }
  }, {
    id: routes.proposalDefaults,
    text: 'Default settings',
    url: routes.proposalDefaults,
    shouldShow: _ref6 => {
      let {
        isVendor,
        role
      } = _ref6;
      return isVendor && !!role && role !== UserRole.Member;
    }
  }]
}, {
  id: 'agreements',
  url: routes.agreementsDashboard,
  text: 'Agreements',
  shouldShow: true
}, {
  id: 'proposals',
  url: routes.proposalsDashboard,
  text: 'Proposals',
  shouldShow: _ref7 => {
    let {
      isVendor
    } = _ref7;
    return !isVendor;
  }
}, {
  id: 'billing',
  url: routes.globalBillingHub,
  text: 'Billing',
  shouldShow: _ref8 => {
    let {
      isVendor
    } = _ref8;
    return isVendor;
  }
}, {
  id: 'invoices',
  url: routes.invoicesDashboard,
  text: 'Invoices',
  shouldShow: true
}, {
  id: 'payouts',
  url: routes.payoutsDashboard,
  text: 'Payouts',
  shouldShow: _ref9 => {
    let {
      isVendor,
      role
    } = _ref9;
    return isVendor && !!role && role !== UserRole.Member;
  }
}, {
  id: 'contacts',
  url: routes.contacts,
  text: 'Contacts',
  shouldShow: _ref10 => {
    let {
      isVendor
    } = _ref10;
    return isVendor;
  },
  exact: true
}, {
  id: 'services',
  url: routes.serviceLibrary,
  text: 'Services',
  shouldShow: _ref11 => {
    let {
      isVendor,
      role
    } = _ref11;
    return isVendor && !!role && role !== UserRole.Member;
  },
  exact: true
}, {
  id: 'reports',
  url: routes.reports,
  text: 'Reports',
  shouldShow: _ref12 => {
    let {
      isVendor,
      ff
    } = _ref12;
    return isVendor && ff.REPORTS === true;
  }
}, {
  id: 'settings',
  text: 'Settings',
  shouldShow: _ref13 => {
    let {
      role
    } = _ref13;
    return !!role && role !== UserRole.Member;
  },
  items: [{
    id: routes.businessDetails,
    text: 'Business details',
    url: routes.businessDetails,
    shouldShow: _ref14 => {
      let {
        role
      } = _ref14;
      return !!role && role !== UserRole.Member;
    }
  }, {
    id: routes.team,
    text: 'Team',
    url: routes.team,
    shouldShow: _ref15 => {
      let {
        role
      } = _ref15;
      return !!role && role !== UserRole.Member;
    }
  }, {
    id: 'integrations',
    url: routes.integrations,
    text: 'Integrations',
    shouldShow: _ref16 => {
      let {
        isVendor,
        role
      } = _ref16;
      return isVendor && !!role && role !== UserRole.Member;
    }
  }, {
    id: routes.notifications,
    url: routes.notifications,
    text: 'Notifications',
    shouldShow: _ref17 => {
      let {
        isVendor,
        role,
        ff
      } = _ref17;
      return !!ff['NOTIFICATION_SETTINGS'] && isVendor && !!role && role !== UserRole.Member;
    }
  }]
}, {
  id: 'referrals',
  url: routes.referrals,
  text: 'Refer & Earn',
  shouldShow: _ref18 => {
    let {
      isVendor
    } = _ref18;
    return isVendor;
  },
  icon: /*#__PURE__*/React.createElement(Gift18, null)
}];
const shouldBeCollapsed = (currentPath, clickedRouteId, route) => !(route.id === clickedRouteId) && !('items' in route && route.items.some(item => 'url' in item && currentPath === item.url));
const SidebarLevel = _ref19 => {
  let {
    items,
    counters,
    logBI
  } = _ref19;
  const location = useLocation();
  const generateCollapseMap = useCallback(uncollapsedGroupId => items.reduce((acc, route) => Object.assign({}, acc, {
    [route.id]: shouldBeCollapsed(location.pathname, uncollapsedGroupId, route)
  }), {}), [items, location]);
  const [collapseMap, setCollapseMap] = useState(generateCollapseMap(null));
  useEffect(() => {
    setCollapseMap(generateCollapseMap(null));
  }, [items, generateCollapseMap]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, items.map(item => 'url' in item ? /*#__PURE__*/React.createElement(Sidebar.Item, {
    as: NavLink,
    key: item.id,
    to: item.url,
    activeClassName: SIDEBAR_ITEM_ACTIVE_CLASS,
    id: item.id,
    after: counters[item.id] || item.icon || undefined,
    "data-testid": "home-sidebar-".concat(item.id),
    onClick: event => {
      logBI(10355, {
        screenName: event.currentTarget.id
      });
      setCollapseMap(generateCollapseMap(null));
    }
  }, item.text) : 'items' in item ? /*#__PURE__*/React.createElement(Sidebar.Group, {
    as: NavLink,
    to: 'url' in item.items[0] && item.items[0].url,
    "data-testid": "home-sidebar-".concat(item.id, "-toggle"),
    id: item.id,
    key: item.id,
    items: /*#__PURE__*/React.createElement(SidebarLevel, {
      items: item.items,
      counters: counters,
      logBI: logBI
    }),
    collapsed: collapseMap[item.id],
    onClick: _event => {
      logBI(10356, {
        groupName: item.id
      });
      setCollapseMap(generateCollapseMap(!collapseMap[item.id] ? null : item.id));
    }
  }, item.text) : null).filter(notEmpty));
};

/** The new design for the app's sidebar when the user is logged-in. */
export default function HomeSidebar() {
  const location = useLocation();
  const globalContext = useGlobalContext();
  const isVendor = useStore(selectors.user.isVendor);
  const sidebarCounters = useStore(state => state.sidebarCounters);
  const {
    fetchSidebarCounters,
    logBI
  } = useStoreActions();
  const debouncedFetchSidebarCounters = useMemo(() => debounce(fetchSidebarCounters, 30), [fetchSidebarCounters]);
  const items = useStore(selectors.filterAvailableRoutes(sidebarRoutes));
  const {
    DISABLE_BILLING_SCAN = false
  } = useFeatureFlags();

  // On mount and on navigation.
  useEffect(() => {
    // Use a minimal debounce to avoid fetching multiple times for a multi-part
    // navigation. For example, when clicking on the logo link.
    debouncedFetchSidebarCounters();
  }, [location.pathname, debouncedFetchSidebarCounters]);
  const onAddChargeClick = () => {
    logBI(10351);
    globalContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref20 => {
        let {
          dismissModal
        } = _ref20;
        return /*#__PURE__*/React.createElement(AddChargeModal, {
          dismissModal: dismissModal
        });
      }
    });
  };
  const onLinkClick = event => {
    logBI(10355, {
      screenName: event.currentTarget.id
    });
  };
  return /*#__PURE__*/React.createElement(Sidebar, {
    "data-testid": "home-sidebar"
  }, /*#__PURE__*/React.createElement(Sidebar.Header, null, /*#__PURE__*/React.createElement(NavLink, {
    id: isVendor ? 'dashboard-via-logo' : 'agreements-via-logo',
    to: "/",
    "data-testid": "home-sidebar-logo-btn",
    onClick: onLinkClick
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.logoContainer
  }, /*#__PURE__*/React.createElement(AnchorLogo, {
    theme: "light",
    height: 44
  })))), /*#__PURE__*/React.createElement(Sidebar.Content, null, /*#__PURE__*/React.createElement(SidebarLevel, {
    items: items,
    counters: sidebarCounters,
    logBI: logBI
  })), /*#__PURE__*/React.createElement(Sidebar.Footer, null, /*#__PURE__*/React.createElement("div", {
    className: styles.footer
  }, isVendor && /*#__PURE__*/React.createElement(Sidebar.Item, {
    id: "add-charge",
    before: /*#__PURE__*/React.createElement(PlusCircleLine, null),
    intent: Intent.AddCharge,
    "data-testid": "home-sidebar-add-charge",
    "data-intercom-target": "Global add charge button",
    onClick: onAddChargeClick,
    disabled: DISABLE_BILLING_SCAN,
    tooltip: DISABLE_BILLING_SCAN ? {
      title: /*#__PURE__*/React.createElement(DisabledBillingTooltip, null),
      placement: 'right'
    } : undefined
  }, "Add charge"))));
}